import React, { useMemo, useState } from "react";

const cleanBaseUrl = (value = "") => String(value || "").trim().replace(/\/+$/, "");

const AccountDeletionPage = () => {
  const [form, setForm] = useState({
    email: "",
    username: "",
    reason: "",
    confirmation: false,
    website: "",
  });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [reference, setReference] = useState("");

  const endpoints = useMemo(() => {
    const apiBase = cleanBaseUrl(process.env.REACT_APP_API_URL || "");
    const primary = apiBase
      ? `${apiBase}/account-deletion-request`
      : "/api/account-deletion-request";

    return Array.from(new Set([primary, "/account-deletion-request"]));
  }, []);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setMessage("");
    setReference("");

    if (!form.confirmation) {
      setStatus("error");
      setMessage("Please confirm that you want to request permanent account deletion.");
      return;
    }

    setStatus("submitting");

    const payload = {
      email: form.email.trim(),
      username: form.username.trim(),
      reason: form.reason.trim(),
      confirmDeletion: true,
      website: form.website,
      source: "weavevo-web-account-deletion",
    };

    let lastError = null;

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (response.status === 404) {
            lastError = new Error("Account deletion endpoint was not found.");
            continue;
          }
          throw new Error(data?.error || "Unable to submit your request right now.");
        }

        setStatus("success");
        setReference(String(data?.reference || ""));
        setMessage(
          data?.message ||
            "Your deletion request has been received. We may contact you to verify account ownership."
        );
        setForm({ email: "", username: "", reason: "", confirmation: false, website: "" });
        return;
      } catch (error) {
        lastError = error;
      }
    }

    setStatus("error");
    setMessage(lastError?.message || "Unable to submit your request right now.");
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background:
        "radial-gradient(circle at 12% 8%, rgba(37,99,235,.12), transparent 32%), radial-gradient(circle at 88% 3%, rgba(20,184,166,.13), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)",
      color: "#0f172a",
      fontFamily:
        "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    shell: {
      width: "min(1120px, calc(100% - 32px))",
      margin: "0 auto",
      padding: "34px 0 64px",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 18,
      marginBottom: 34,
    },
    brand: { display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "#0f172a" },
    logo: { width: 48, height: 48, objectFit: "contain" },
    brandName: { fontSize: 25, fontWeight: 760, letterSpacing: "-.035em" },
    privacyLink: {
      color: "#334155",
      textDecoration: "none",
      fontSize: 14,
      padding: "10px 14px",
      borderRadius: 999,
      border: "1px solid #dbe3ef",
      background: "rgba(255,255,255,.78)",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.05fr) minmax(340px, .95fr)",
      gap: 28,
      alignItems: "start",
    },
    card: {
      background: "rgba(255,255,255,.94)",
      border: "1px solid rgba(15,23,42,.08)",
      borderRadius: 28,
      boxShadow: "0 28px 75px rgba(15,23,42,.10)",
    },
    infoCard: { padding: "clamp(26px, 5vw, 54px)" },
    formCard: { padding: "28px" },
    eyebrow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      borderRadius: 999,
      padding: "7px 11px",
      background: "#eff6ff",
      color: "#1d4ed8",
      fontSize: 13,
      fontWeight: 700,
      marginBottom: 18,
    },
    title: {
      margin: "0 0 16px",
      fontSize: "clamp(38px, 6vw, 66px)",
      lineHeight: 1.02,
      letterSpacing: "-.055em",
      fontWeight: 760,
    },
    lead: { margin: "0 0 28px", color: "#475569", fontSize: 18, lineHeight: 1.65 },
    sectionTitle: { margin: "28px 0 10px", fontSize: 18, letterSpacing: "-.02em" },
    list: { margin: 0, paddingLeft: 20, color: "#475569", lineHeight: 1.65 },
    note: {
      marginTop: 28,
      padding: "16px 18px",
      borderRadius: 18,
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      color: "#475569",
      lineHeight: 1.55,
      fontSize: 14,
    },
    formTitle: { margin: "0 0 8px", fontSize: 26, letterSpacing: "-.035em" },
    formIntro: { margin: "0 0 22px", color: "#64748b", lineHeight: 1.5 },
    label: { display: "block", margin: "0 0 8px", fontSize: 14, fontWeight: 700, color: "#334155" },
    input: {
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid #cbd5e1",
      borderRadius: 14,
      padding: "13px 14px",
      fontSize: 16,
      outline: "none",
      background: "#ffffff",
      color: "#0f172a",
    },
    field: { marginBottom: 18 },
    help: { margin: "7px 0 0", fontSize: 12, color: "#64748b", lineHeight: 1.45 },
    checkRow: { display: "flex", gap: 11, alignItems: "flex-start", margin: "6px 0 20px", color: "#334155", lineHeight: 1.5 },
    button: {
      width: "100%",
      border: 0,
      borderRadius: 999,
      padding: "15px 20px",
      background: "linear-gradient(135deg, #111827 0%, #2563eb 60%, #0f766e 100%)",
      color: "#ffffff",
      fontSize: 16,
      fontWeight: 750,
      cursor: "pointer",
      boxShadow: "0 16px 36px rgba(37,99,235,.22)",
    },
    alertSuccess: {
      marginBottom: 18,
      padding: "14px 16px",
      borderRadius: 16,
      background: "#ecfdf5",
      border: "1px solid #a7f3d0",
      color: "#065f46",
      lineHeight: 1.5,
    },
    alertError: {
      marginBottom: 18,
      padding: "14px 16px",
      borderRadius: 16,
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#991b1b",
      lineHeight: 1.5,
    },
    footer: { marginTop: 28, textAlign: "center", color: "#64748b", fontSize: 13 },
  };

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <header style={styles.header}>
          <a href="/" style={styles.brand} aria-label="Weavevo home">
            <img
              src="/assets/weavevo_logo_mark.png"
              alt="Weavevo"
              style={styles.logo}
              onError={(event) => { event.currentTarget.style.display = "none"; }}
            />
            <span style={styles.brandName}>Weavevo</span>
          </a>
          <a href="/politique-confidentialite" style={styles.privacyLink}>Privacy policy</a>
        </header>

        <main style={styles.grid} className="wv-account-deletion-grid">
          <section style={{ ...styles.card, ...styles.infoCard }}>
            <div style={styles.eyebrow}>Account &amp; data</div>
            <h1 style={styles.title}>Request deletion of your Weavevo account</h1>
            <p style={styles.lead}>
              Use this page to request permanent deletion of your Weavevo account and the personal data associated with it.
              We may contact you at the account email address to verify ownership before processing the request.
            </p>

            <h2 style={styles.sectionTitle}>Data deleted or anonymized</h2>
            <ul style={styles.list}>
              <li>Your Weavevo profile, account identifiers and authentication account.</li>
              <li>Personal settings, saved preferences and private account information.</li>
              <li>Private media and content that belongs only to your account.</li>
              <li>Collaborative contributions where deletion is possible; otherwise identifying details are anonymized.</li>
            </ul>

            <h2 style={styles.sectionTitle}>Limited data that may be retained</h2>
            <ul style={styles.list}>
              <li>Encrypted backup copies may remain for up to 30 days before automatic removal.</li>
              <li>Security and abuse-prevention logs may be retained for up to 12 months.</li>
              <li>Invoices, payment and accounting records may be retained for up to 10 years where required by law.</li>
            </ul>

            <div style={styles.note}>
              Submitting this form starts a deletion request; it does not instantly delete the account. After identity verification,
              the Weavevo team will process the request and confirm completion by email. This page is available without signing in.
            </div>
          </section>

          <section style={{ ...styles.card, ...styles.formCard }} aria-labelledby="deletion-form-title">
            <h2 id="deletion-form-title" style={styles.formTitle}>Submit your request</h2>
            <p style={styles.formIntro}>Enter the email address used for your Weavevo account.</p>

            {status === "success" && (
              <div role="status" style={styles.alertSuccess}>
                <strong>Request received.</strong><br />
                {message}
                {reference ? <><br />Reference: <strong>{reference}</strong></> : null}
              </div>
            )}

            {status === "error" && (
              <div role="alert" style={styles.alertError}>{message}</div>
            )}

            <form onSubmit={submitRequest}>
              <div style={styles.field}>
                <label htmlFor="deletion-email" style={styles.label}>Account email *</label>
                <input
                  id="deletion-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  maxLength={254}
                  value={form.email}
                  onChange={onChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="deletion-username" style={styles.label}>Username (optional)</label>
                <input
                  id="deletion-username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  maxLength={150}
                  value={form.username}
                  onChange={onChange}
                  style={styles.input}
                />
                <p style={styles.help}>Add it when your username differs from your email address.</p>
              </div>

              <div style={styles.field}>
                <label htmlFor="deletion-reason" style={styles.label}>Additional information (optional)</label>
                <textarea
                  id="deletion-reason"
                  name="reason"
                  rows={5}
                  maxLength={2000}
                  value={form.reason}
                  onChange={onChange}
                  style={{ ...styles.input, resize: "vertical", minHeight: 120 }}
                />
              </div>

              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                <label htmlFor="deletion-website">Website</label>
                <input
                  id="deletion-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={onChange}
                />
              </div>

              <label style={styles.checkRow}>
                <input
                  name="confirmation"
                  type="checkbox"
                  checked={form.confirmation}
                  onChange={onChange}
                  style={{ marginTop: 4, width: 18, height: 18 }}
                />
                <span>
                  I request permanent deletion of my Weavevo account and understand that this action cannot be undone after processing.
                </span>
              </label>

              <button type="submit" disabled={status === "submitting"} style={{ ...styles.button, opacity: status === "submitting" ? .68 : 1 }}>
                {status === "submitting" ? "Submitting…" : "Request account deletion"}
              </button>
            </form>
          </section>
        </main>

        <footer style={styles.footer}>© {new Date().getFullYear()} Weavevo — Account deletion request</footer>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .wv-account-deletion-grid { grid-template-columns: 1fr !important; }
        }
        input:focus, textarea:focus { border-color: #2563eb !important; box-shadow: 0 0 0 4px rgba(37,99,235,.10); }
        button:disabled { cursor: wait !important; }
      `}</style>
    </div>
  );
};

export default AccountDeletionPage;
